import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { BookOpen, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';


export default function AuthCallback() {
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check for error in query params first
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        
        if (error) {
          setStatus('error');
          setMessage(errorDescription || error || 'Authentication failed');
          return;
        }

        // Get hash fragment params (Supabase uses hash for tokens)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');
        const errorInHash = hashParams.get('error');
        const errorDescInHash = hashParams.get('error_description');

        // Check for errors in hash
        if (errorInHash) {
          setStatus('error');
          setMessage(errorDescInHash || errorInHash || 'Authentication failed');
          return;
        }

        // If we have tokens, set the session
        if (accessToken && refreshToken) {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (sessionError) {
            console.error('Session error:', sessionError);
            setStatus('error');
            setMessage('Failed to verify your account. Please try logging in.');
            return;
          }

          // Success - email confirmed and session set
          setStatus('success');
          if (type === 'signup' || type === 'email_change') {
            setMessage('Email confirmed successfully! Redirecting to dashboard...');
          } else if (type === 'recovery') {
            setMessage('Password reset confirmed! Redirecting...');
          } else {
            setMessage('Authentication successful! Redirecting...');
          }
          
          setTimeout(() => navigate('/dashboard'), 1500);
          return;
        }

        // No tokens in URL - check if user already has a session
        const { data: sessionData } = await supabase.auth.getSession();
        
        if (sessionData?.session) {
          setStatus('success');
          setMessage('You are already logged in! Redirecting...');
          setTimeout(() => navigate('/dashboard'), 1500);
          return;
        }

        // Check for token_hash in URL (email confirmation link format)
        const tokenHash = searchParams.get('token_hash');
        const tokenType = searchParams.get('type');
        
        if (tokenHash && tokenType) {
          // This is an email confirmation link, verify it
          const { error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: tokenType
          });

          if (verifyError) {
            console.error('Verification error:', verifyError);
            setStatus('error');
            setMessage('This confirmation link has expired or is invalid. Please request a new one or try logging in.');
            return;
          }

          setStatus('success');
          setMessage('Email confirmed! Please log in to continue.');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        // No tokens, no session, no token_hash - just redirect to login
        setStatus('success');
        setMessage('Please log in to continue.');
        setTimeout(() => navigate('/login'), 1500);

      } catch (err) {
        console.error('Auth callback error:', err);
        setStatus('error');
        setMessage('An unexpected error occurred. Please try logging in.');
      }
    };

    handleCallback();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b-2 border-border">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 w-fit">
            <div className="w-10 h-10 bg-primary flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-black text-lg tracking-tight">CALC AI</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="border-2 border-border p-8">
            {status === 'loading' && (
              <>
                <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
                <h1 className="font-heading font-bold text-xl mb-2">Verifying...</h1>
                <p className="text-muted-foreground">Please wait while we confirm your account.</p>
              </>
            )}
            
            {status === 'success' && (
              <>
                <div className="w-16 h-16 bg-green-500 mx-auto mb-4 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h1 className="font-heading font-bold text-xl mb-2">Success!</h1>
                <p className="text-muted-foreground mb-4">{message}</p>
                <p className="text-sm text-muted-foreground">Redirecting...</p>
              </>
            )}
            
            {status === 'error' && (
              <>
                <div className="w-16 h-16 bg-destructive mx-auto mb-4 flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-white" />
                </div>
                <h1 className="font-heading font-bold text-xl mb-2">Oops!</h1>
                <p className="text-muted-foreground mb-6">{message}</p>
                <div className="space-y-3">
                  <Link to="/login">
                    <Button className="w-full h-12 font-bold uppercase">
                      Go to Login
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button variant="outline" className="w-full h-12 border-2">
                      Create New Account
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
       {/* Footer */}
      <footer className="border-t-2 border-border py-8 px-6 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-bold text-sm">CALC AI</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © Powered by FHYAS LLC.
          </p>
        </div>
      </footer>
      </main>
    </div>
  );
}
